// src/api/users.api.ts — SCE Issues
// Résolution d'un UPN Azure AD vers son LookupId SharePoint.
//
// ─── Stratégie ───
//
// Sur le tenant Sartorius, l'approche Graph via "User Information List" est
// instable (400 Bad Request sur le nom avec espace, accès limité par ID).
// On bascule sur SharePoint REST avec /_api/web/ensureUser.
//
// ensureUser :
//   - Si l'utilisateur est déjà connu du site → renvoie son Id existant
//   - Sinon → SharePoint le crée à partir d'Azure AD et renvoie le nouvel Id
//
// Le payload exige un `logonName` au format claims fédéré moderne :
//   i:0#.f|membership|<upn>
//
// Ce format couvre les comptes internes (membership) du tenant Sartorius.
// Les guests B2B (#EXT#) utilisent un format légèrement différent — non
// adressé ici puisque les coordinateurs sont tous internes.

import { sharepointPost, SharePointApiError } from './sharepointClient'

// ─── Configuration ───

/**
 * URL complète du site SharePoint (telle que documentée dans .env.local).
 * On en extrait le pathname pour les appels REST.
 *
 * Source unique de vérité côté env : VITE_SHAREPOINT_SITE_URL.
 * Fallback en dur sur OP_SCE si la variable n'est pas définie.
 */
const SITE_URL =
  import.meta.env.VITE_SHAREPOINT_SITE_URL ??
  'https://sartorius.sharepoint.com/sites/OP_SCE'

/**
 * Chemin du site (ex: "/sites/OP_SCE") extrait de l'URL complète.
 * Utilisé par sharepointClient pour construire l'URL des appels REST.
 */
const SITE_PATH = new URL(SITE_URL).pathname

/**
 * Construit le logonName fédéré pour un UPN donné.
 * Format attendu par ensureUser pour les comptes Azure AD modernes.
 */
function buildLogonName(upn: string): string {
  return `i:0#.f|membership|${upn}`
}

// ─── Cache ───
//
// Clé : UPN normalisé en lowercase
// Valeur : LookupId numérique (Id SharePoint dans la User Information List)
//
// Le cache est volatile (mémoire seulement) — survit à la durée d'une session
// utilisateur. Les LookupIds SharePoint sont stables, donc safe à cacher.

const cacheLookup = new Map<string, number>()

export function clearUserLookupCache(): void {
  cacheLookup.clear()
}

// ─── Type retourné par ensureUser ───

/**
 * Réponse SharePoint REST pour ensureUser (format JSON verbose).
 * Seul `Id` nous intéresse — c'est le LookupId à écrire dans field_15LookupId.
 */
interface EnsureUserResponse {
  Id: number
  LoginName: string
  Title?: string
  Email?: string
}

// ─── API publique ───

/**
 * Résout un UPN Azure AD en LookupId SharePoint via ensureUser.
 *
 * Comportement :
 *   - Cache hit → retour immédiat
 *   - Cache miss → POST /_api/web/ensureUser
 *   - Erreur (utilisateur introuvable, 403, etc.) → null + log warning
 *
 * @param upn userPrincipalName Azure AD (ex: thibaut.hacquin@sartorius.com)
 * @returns LookupId numérique SharePoint, ou null si la résolution échoue
 */
export async function resolveUserLookupId(
  upn: string,
): Promise<number | null> {
  if (!upn) return null

  const key = upn.toLowerCase()

  // Cache hit
  const cached = cacheLookup.get(key)
  if (cached !== undefined) {
    return cached
  }

  // ensureUser via SharePoint REST
  try {
    const response = await sharepointPost<EnsureUserResponse>(
      SITE_PATH,
      'web/ensureUser',
      { logonName: buildLogonName(upn) },
    )

    if (typeof response?.Id === 'number') {
      cacheLookup.set(key, response.Id)
      return response.Id
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[users.api] ensureUser a retourné une réponse sans Id pour ${upn}:`,
      response,
    )
    return null
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      `[users.api] ensureUser a échoué pour ${upn}:`,
      err instanceof SharePointApiError
        ? `${err.status} ${err.message}`
        : err,
    )
    return null
  }
}

/**
 * Résout plusieurs UPN en parallèle.
 *
 * Utilisé pour le multi-coordinateur (ex: MECA avec Romuald + Sébastien).
 * Toutes les résolutions sont lancées en parallèle, les échecs ne bloquent
 * pas les autres.
 *
 * @returns Map<upn-lowercase, LookupId | null>
 */
export async function resolveUserLookupIds(
  upns: string[],
): Promise<Map<string, number | null>> {
  const result = new Map<string, number | null>()

  await Promise.all(
    upns.map(async (upn) => {
      const id = await resolveUserLookupId(upn)
      result.set(upn.toLowerCase(), id)
    }),
  )

  return result
}
